export class CookieService {
  name;
  expiresString;
  pathString;
  domainString;
  constructor(name) {
    this.name = name;
    this.path("/");
  }
  /**
   * Prepares the statement to let a cookie expire in the specified amount of days.
   */
  expiresInDays(days) {
    const d = /* @__PURE__ */ new Date();
    d.setTime(d.getTime() + days * 24 * 60 * 60 * 1e3);
    this.expiresString = `expires=${d.toUTCString()}`;
    return this;
  }
  /**
   * Prepares the statement to let the cookie belong to a specific path.
   */
  path(path) {
    this.pathString = `path=${path}`;
    return this;
  }
  /**
   * Prepares the statement to let the cookie belong to a specific domain.
   */
  domain(domain) {
    this.domainString = `domain=${domain}`;
    return this;
  }
  /**
   * Prepares the statement to let the cookie belong to all subdomains of the main host, e.g., spoferan.com.
   */
  onAllDomains() {
    const hostname = window.location.hostname;
    const hostnameParts = hostname.split(".");
    if (["localhost", "192"].includes(hostnameParts[0])) {
      return this.domain(hostname);
    }
    return this.domain(hostnameParts[hostnameParts.length - 2] + "." + hostnameParts[hostnameParts.length - 1]);
  }
  /**
   * Sets the cookie with the specified value.
   */
  set(value) {
    const cookieParts = [`${this.name}=${value}`];
    if (this.expiresString) {
      cookieParts.push(this.expiresString);
    }
    if (this.pathString) {
      cookieParts.push(this.pathString);
    }
    if (this.domainString) {
      cookieParts.push(this.domainString);
    }
    document.cookie = cookieParts.join("; ");
  }
  /**
   * Removes the cookie.
   */
  remove() {
    const cookieParts = [`${this.name}=`, "expires=Thu, 01 Jan 1970 00:00:00 UTC"];
    if (this.pathString) {
      cookieParts.push(this.pathString);
    }
    if (this.domainString) {
      cookieParts.push(this.domainString);
    }
    document.cookie = cookieParts.join("; ");
  }
  /**
   * Gets the cookie value if any is set.
   */
  get() {
    const nameString = this.name + "=";
    const decodedCookie = decodeURIComponent(document.cookie);
    const ca = decodedCookie.split(";");
    for (let i = 0; i < ca.length; i++) {
      let c = ca[i];
      while (c.charAt(0) === " ") {
        c = c.substring(1);
      }
      if (c.indexOf(nameString) === 0) {
        return c.substring(nameString.length, c.length);
      }
    }
    return null;
  }
  /**
   * Sets the cookie as a JSON object with the specified key and value.
   */
  setObjectValue(key, value) {
    const valueObject = this.getAsObject() ?? {};
    valueObject[key] = value;
    this.set(JSON.stringify(valueObject));
  }
  /**
   * Some cookies are stored as encoded JSON objects, see setObjectValue().
   * This method returns the full object value stored under the cookie name.
   */
  getAsObject() {
    const valueObject = this.get();
    if (valueObject) {
      try {
        const parsedObject = JSON.parse(valueObject);
        if (parsedObject) {
          return parsedObject;
        }
      } catch (e) {
        console.error(e);
      }
    }
    return null;
  }
  /**
   * Some cookies are stored as encoded JSON objects, see setObjectValue().
   * This method returns the value of the specified key on the object, if it exists.
   */
  getObjectValue(key) {
    const valueObject = this.getAsObject() ?? {};
    return valueObject[key] ?? null;
  }
  /**
   * Removes the specified key value pair of the cookie object.
   * If this is the last key value pair, the cookie is removed.
   */
  removeObjectValue(key) {
    const valueObject = this.getAsObject() ?? {};
    delete valueObject[key];
    if (Object.keys(valueObject).length === 0) {
      this.remove();
    } else {
      this.set(JSON.stringify(valueObject));
    }
  }
}
